import { useEffect, useState } from "react";
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, Button, Divider, LinearProgress, IconButton, Tooltip, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch, TextField } from "@mui/material";
import SavingsIcon from "@mui/icons-material/Savings";
import PeopleIcon from "@mui/icons-material/People";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { budgetService } from "@/api/budget.service";
import { useAuth } from "@/context/AuthContext";
import type { BudgetBreakdown, BudgetSummary } from "@/api/types";

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
const fmt = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

function Row({ label, value, bold, color, tooltip }: { label: string; value: string; bold?: boolean; color?: string; tooltip?: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" fontWeight={bold ? 700 : 400} color={color || "text.primary"}>{value}</Typography>
    </Box>
  );
}

function ExpandableRow({
  label, value, bold, color, tooltip, details, expanded, onToggle,
}: {
  label: string; value: string; bold?: boolean; color?: string; tooltip?: string;
  details: { label: string; value: string }[]; expanded: boolean; onToggle: () => void;
}) {
  return (
    <Box>
      <Box
        onClick={onToggle}
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25, cursor: "pointer", userSelect: "none" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ExpandMoreIcon
            sx={{ fontSize: 18, color: "text.disabled", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
          />
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoOutlinedIcon
                sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                onClick={e => e.stopPropagation()}
              />
            </Tooltip>
          )}
        </Box>
        <Typography variant="body2" fontWeight={bold ? 700 : 400} color={color || "text.primary"}>{value}</Typography>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 3, pr: 0.5, pb: 0.5 }}>
          {details.map(d => (
            <Box key={d.label} sx={{ display: "flex", justifyContent: "space-between", py: 0.15 }}>
              <Typography variant="caption" color="text.secondary">{d.label}</Typography>
              <Typography variant="caption" color="text.secondary">{d.value}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function PersonBreakdownCard({ b, isMe }: { b: BudgetBreakdown; isMe: boolean }) {
  const [paidExpanded, setPaidExpanded] = useState(false);
  const [totalExpanded, setTotalExpanded] = useState(false);

  const sharedPaid = b.sharedActuallyPaid ?? 0;
  const totalPaid = sharedPaid + b.personalExpenses;
  const bal = b.settlementBalance ?? 0;

  return (
    <Paper elevation={isMe ? 3 : 1} sx={{ p: 3, borderRadius: 3, border: isMe ? "2px solid" : "none", borderColor: "primary.main" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography fontWeight={700}>{b.name}</Typography>
        {isMe && <Chip label="Moi" size="small" color="primary" />}
      </Box>
      <Typography variant="body2" color="text.secondary">Revenu mensuel</Typography>
      <Typography variant="h6" fontWeight={600}>{fmt(b.income)}</Typography>
      <LinearProgress variant="determinate" value={parseFloat(b.incomeRatio)} sx={{ my: 1.5, height: 8, borderRadius: 4 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">Part du foyer : {b.incomeRatio}</Typography>
        <Tooltip title="Votre part des revenus totaux du foyer. Elle détermine la proportion des charges partagées que vous devez couvrir." arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", cursor: "help" }} />
        </Tooltip>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <ExpandableRow
        label="Charges payées"
        value={fmt(totalPaid)}
        tooltip="Montant total que vous avez payé ce mois-ci, dépenses partagées et personnelles confondues. Cliquez pour voir le détail."
        details={[
          { label: "Partagées", value: fmt(sharedPaid) },
          { label: "Personnelles", value: fmt(b.personalExpenses) },
        ]}
        expanded={paidExpanded}
        onToggle={() => setPaidExpanded(v => !v)}
      />
      {Math.abs(bal) >= 0.01 && (
        <Row
          label={bal > 0 ? "Avance à récupérer" : "Reste à payer"}
          value={fmt(Math.abs(bal))}
          color={bal > 0 ? "success.main" : "error.main"}
          tooltip={bal > 0
            ? "Vous avez payé plus que votre part. L'autre membre vous doit cette somme."
            : "Vous avez payé moins que votre part. Vous devez cette somme à l'autre membre."}
        />
      )}
      <Divider sx={{ my: 1 }} />
      <ExpandableRow
        label="Total à payer"
        value={fmt(b.totalToPay)}
        bold
        tooltip="Part équitable des charges partagées + vos dépenses personnelles. C'est ce que vous devez couvrir ce mois-ci. Cliquez pour voir le détail."
        details={[
          { label: "Part équitable", value: fmt(b.sharedContribution) },
          { label: "Dépenses perso", value: fmt(b.personalExpenses) },
        ]}
        expanded={totalExpanded}
        onToggle={() => setTotalExpanded(v => !v)}
      />
      <Row
        label="Balance du mois"
        value={fmt(b.remainingAfterExpenses)}
        color={b.remainingAfterExpenses >= 0 ? "success.main" : "error.main"}
        tooltip="Votre revenu mensuel moins votre total à payer. Un montant négatif signifie que vos dépenses dépassent vos revenus."
      />
    </Paper>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [viewYear, setViewYear] = useState(now.getFullYear());

  // Archive modal state
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");

  const isCurrentMonth = viewMonth === now.getMonth() + 1 && viewYear === now.getFullYear();

  useEffect(() => {
    setLoading(true);
    setSummary(null);
    setError("");
    setSaved(false);
    budgetService.getSummary(viewMonth, viewYear).then(setSummary).catch(e => setError(e.response?.data?.error || "Erreur.")).finally(() => setLoading(false));
  }, [viewMonth, viewYear]);

  const prevMonth = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const openArchiveModal = () => {
    if (!summary) return;
    const [a] = summary.breakdown;
    const balanceA = a.settlementBalance ?? 0;
    const suggested = Math.abs(balanceA);
    setPaymentDone(false);
    setPaymentAmount(suggested > 0 ? String(suggested) : "");
    setArchiveOpen(true);
  };

  const handleSnapshot = async () => {
    if (!summary) return;
    setSaving(true);
    setArchiveOpen(false);
    try {
      const [a] = summary.breakdown;
      const balanceA = a.settlementBalance ?? 0;
      const creditor = balanceA >= 0 ? a : summary.breakdown[1];
      const debtor = balanceA >= 0 ? summary.breakdown[1] : a;
      const amount = parseFloat(paymentAmount);
      const payment = paymentDone && !isNaN(amount) && amount > 0
        ? { fromUserId: debtor.userId, fromName: debtor.name, toUserId: creditor.userId, toName: creditor.name, amount }
        : null;
      await budgetService.saveSnapshot(viewMonth, viewYear, payment);
      setSaved(true);
    } catch (e: any) {
      setError(e.response?.data?.error || "Erreur.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;

  const summaryCards = summary ? [
    {
      icon: <PeopleIcon />,
      label: "Revenus du foyer",
      value: fmt(summary.totalIncome),
      color: "primary.main",
      tooltip: "Somme des revenus mensuels des deux membres du foyer pour ce mois.",
    },
    {
      icon: <ReceiptLongIcon />,
      label: "Charges partagées",
      value: fmt(summary.sharedExpenses.total),
      color: "warning.main",
      tooltip: "Total des dépenses partagées du foyer (récurrentes + ponctuelles) réparties proportionnellement aux revenus de chacun.",
    },
    {
      icon: <SavingsIcon />,
      label: "Dépenses ponctuelles",
      value: fmt(summary.sharedExpenses.occasional),
      color: "info.main",
      tooltip: "Part ponctuelle (non récurrente) des charges partagées ce mois-ci, incluse dans le total des charges partagées ci-dessus.",
    },
  ] : [];

  // Settlement derived values (used in modal + dashboard card)
  const settlementInfo = summary ? (() => {
    const [a, b] = summary.breakdown;
    const balanceA = a.settlementBalance ?? 0;
    const amount = Math.abs(balanceA);
    const creditor = balanceA >= 0 ? a : b;
    const debtor = balanceA >= 0 ? b : a;
    return { amount, creditor, debtor, isSettled: amount < 0.01 };
  })() : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" onClick={prevMonth}><ChevronLeftIcon /></IconButton>
          <Typography variant="h5" fontWeight={700} sx={{ minWidth: 140, textAlign: "center" }}>
            {MONTHS[viewMonth - 1]} {viewYear}
          </Typography>
          <IconButton size="small" onClick={nextMonth} disabled={isCurrentMonth}><ChevronRightIcon /></IconButton>
          {!isCurrentMonth && <Chip label="Passé" size="small" variant="outlined" sx={{ ml: 1 }} />}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SavingsIcon />}
          onClick={summary ? openArchiveModal : undefined}
          disabled={saving || saved || !summary}
        >
          {saved ? "Archivé ✓" : saving ? "Archivage…" : "Archiver le mois"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!summary ? <Alert severity="info">Aucune donnée. Renseignez vos revenus et dépenses.</Alert> : (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map(({ icon, label, value, color, tooltip }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1, color }}>
                    {icon}
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Tooltip title={tooltip} arrow placement="top">
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h6" fontWeight={700}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Settlement card */}
          {settlementInfo && (() => {
            const { amount, creditor, debtor, isSettled } = settlementInfo;
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Règlement du mois</Typography>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: isSettled ? "success.dark" : "warning.dark",
                    background: isSettled
                      ? "linear-gradient(135deg, rgba(46,125,50,0.12), rgba(46,125,50,0.04))"
                      : "linear-gradient(135deg, rgba(237,108,2,0.12), rgba(237,108,2,0.04))",
                  }}
                >
                  {isSettled ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 32 }} />
                      <Box>
                        <Typography fontWeight={700} color="success.main">Comptes équilibrés</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Chacun a payé exactement sa part des charges partagées ce mois-ci.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                        <SwapHorizIcon sx={{ color: "warning.main", fontSize: 32, flexShrink: 0 }} />
                        <Box>
                          <Typography fontWeight={700}>
                            <Box component="span" color="error.main">{debtor.name}</Box>
                            {" doit "}
                            <Box component="span" color="success.main" fontWeight={800}>{fmt(amount)}</Box>
                            {" à "}
                            <Box component="span" color="primary.main">{creditor.name}</Box>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {creditor.name} a avancé plus que sa part équitable. Ce virement rééquilibre les comptes.
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        {summary.breakdown.map((bd) => {
                          const bal = bd.settlementBalance ?? 0;
                          return (
                            <Grid item xs={6} key={bd.userId}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{bd.name}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                A payé : {fmt(bd.sharedActuallyPaid ?? 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Sa part : {fmt(bd.sharedContribution)}
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color={bal >= 0 ? "success.main" : "error.main"}
                                display="block"
                              >
                                {bal >= 0.01
                                  ? `Avance de ${fmt(bal)}`
                                  : bal <= -0.01
                                  ? `Doit ${fmt(Math.abs(bal))}`
                                  : "Équilibré"}
                              </Typography>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              </Box>
            );
          })()}

          {/* Per-person breakdown */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Répartition équitable</Typography>
          <Grid container spacing={2}>
            {summary.breakdown.map(b => (
              <Grid item xs={12} sm={6} key={b.userId}>
                <PersonBreakdownCard b={b} isMe={b.userId === user?.id} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Archive modal */}
      {summary && settlementInfo && (
        <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle fontWeight={700}>
            Archiver — {MONTHS[viewMonth - 1]} {viewYear}
          </DialogTitle>
          <DialogContent dividers>
            {/* Settlement recap */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Règlement des charges partagées
            </Typography>
            {settlementInfo.isSettled ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
                <Typography variant="body2" color="success.main" fontWeight={600}>
                  Comptes équilibrés — aucun virement nécessaire.
                </Typography>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Selon la répartition,{" "}
                  <Box component="span" fontWeight={700} color="error.main">{settlementInfo.debtor.name}</Box>
                  {" doit "}
                  <Box component="span" fontWeight={700} color="success.main">{fmt(settlementInfo.amount)}</Box>
                  {" à "}
                  <Box component="span" fontWeight={700} color="primary.main">{settlementInfo.creditor.name}</Box>.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settlementInfo.creditor.name} a avancé plus que sa part proportionnelle.
                </Typography>
              </Paper>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Payment confirmation */}
            {!settlementInfo.isSettled && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={paymentDone}
                      onChange={e => setPaymentDone(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Le règlement a été effectué
                    </Typography>
                  }
                  sx={{ mb: paymentDone ? 2 : 0 }}
                />
                {paymentDone && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <TextField
                      label="Montant versé (€)"
                      type="number"
                      size="small"
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 160 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      versé par{" "}
                      <Box component="span" fontWeight={600}>{settlementInfo.debtor.name}</Box>
                      {" à "}
                      <Box component="span" fontWeight={600}>{settlementInfo.creditor.name}</Box>
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setArchiveOpen(false)} color="inherit">Annuler</Button>
            <Button
              variant="contained"
              startIcon={<SavingsIcon />}
              onClick={handleSnapshot}
              disabled={saving}
            >
              Archiver le mois
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
